import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import {Formik} from "formik";
import * as yup from "yup";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import QButton from "../../../../common/components/QButton";
import QSelect from "../../../../common/components/QSelect";
import SetupApi from "../../../../common/api/functions/Setup";
import {config} from "../../../../config";
import {setToast} from "../../../../app/rootReducer";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {SetupInterface} from "../../../../interfaces/private/SetupInterface";
import {SelectOptionInterface} from "../../../../interfaces/SelectOptionInterface";
import {
    AccountInterface,
    JournalInterface,
} from "../../../../interfaces/economic/CommonInterface";

interface Props {
    model: SetupInterface;
    setOpen: Function;
    setLoadingFunc: Function;
    setSetup: Function;
    setup: SetupInterface;
    allDataSetup: any;
}

const BodySubscriptionSetupComponent: React.FC<Props> = ({
    allDataSetup,
    setOpen,
    setLoadingFunc,
    setSetup,
    setup,
}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const cashbooks: JournalInterface[] = allDataSetup?.cashbooks || [];
    const accounts: AccountInterface[] = allDataSetup?.accounts || [];

    useEffect(() => {
        if (!loading) {
            return;
        }
        SetupApi.detail().then(async (response: ResponseInterface | any) => {
            if (response?.status === config.STATUS_CODE.OK) {
                setSetup(response?.data?.data || {});
            }
        }).finally(async () => {
            await setLoading(false);
        });
    }, [loading, setSetup]);

    return (
        <React.Fragment>
            <Formik
                enableReinitialize
                initialValues={{...setup}}
                validationSchema={yup.object().shape({
                    economic_subscription_cashbook_number: yup
                        .string()
                        .required(t("economic.subscriptionCashbookRequired")),
                    economic_subscription_balance_account_number: yup
                        .string()
                        .required(t("economic.subscriptionBalanceRequired")),
                    economic_subscription_sales_account_number: yup
                        .string()
                        .required(t("economic.subscriptionSalesRequired")),
                })}
                onSubmit={(values: any) => {
                    SetupApi.update(values).then(async (response: ResponseInterface | any) => {
                        if (response.status === config.STATUS_CODE.OK) {
                            setSetup(response?.data?.data || {});
                            await setOpen(false);
                            await setLoadingFunc(true);
                            await dispatch(setToast({
                                message: t("economic.updateSuccess"),
                                type: config.TOAST_TYPE.SUCCESS,
                            }));
                        }
                    });
                }}
            >
                {(formik) => (
                    <React.Fragment>
                        <Row>
                            <Col>
                                <p className="text-muted small mb-3">
                                    {t("economic.sectionSubscriptionHelp")}
                                </p>
                                <QSelect
                                    menuPortal
                                    label={t("economic.cashbook")}
                                    value={parseInt(formik.values.economic_subscription_cashbook_number)}
                                    onChange={(option: SelectOptionInterface) => {
                                        formik.setFieldValue(
                                            "economic_subscription_cashbook_number",
                                            option.value,
                                        );
                                    }}
                                    options={cashbooks.map((a) => ({
                                        label: a.name,
                                        value: a.journalNumber,
                                    }))}
                                    error={formik.errors?.economic_subscription_cashbook_number}
                                />
                                <QSelect
                                    menuPortal
                                    label={t("economic.balanceAccount")}
                                    value={parseInt(formik.values.economic_subscription_balance_account_number)}
                                    onChange={(option: SelectOptionInterface) => {
                                        formik.setFieldValue(
                                            "economic_subscription_balance_account_number",
                                            option.value,
                                        );
                                    }}
                                    options={accounts.map((a) => ({
                                        label: `${a.accountNumber} - ${a.name}`,
                                        value: a.accountNumber,
                                    }))}
                                    error={formik.errors?.economic_subscription_balance_account_number}
                                />
                                <p className="text-muted small mb-2">
                                    {t("economic.subscriptionBalanceHelp")}
                                </p>
                                <QSelect
                                    menuPortal
                                    label={t("economic.salesAccount")}
                                    value={parseInt(formik.values.economic_subscription_sales_account_number)}
                                    onChange={(option: SelectOptionInterface) => {
                                        formik.setFieldValue(
                                            "economic_subscription_sales_account_number",
                                            option.value,
                                        );
                                    }}
                                    options={accounts.map((a) => ({
                                        label: `${a.accountNumber} - ${a.name}`,
                                        value: a.accountNumber,
                                    }))}
                                    error={formik.errors?.economic_subscription_sales_account_number}
                                />
                                <p className="text-muted small mb-0">
                                    {t("economic.subscriptionSalesHelp")}
                                </p>
                            </Col>
                        </Row>
                        <div className="text-end mt-3">
                            <QButton
                                type="secondary"
                                onClick={() => setOpen(false)}
                                content={t("common.button.cancel")}
                            />
                            <QButton
                                type="primary"
                                onClick={() => formik.handleSubmit()}
                                content={t("common.button.save")}
                            />
                        </div>
                    </React.Fragment>
                )}
            </Formik>
        </React.Fragment>
    );
};

export default BodySubscriptionSetupComponent;
